import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';

/**
 * AutopilotView — Autopilot messaging visibility.
 *
 * Role-scoped:
 *  - salesperson: only autopilot jobs for leads THEY have claimed
 *    (GET /api/leads/my-autopilot-jobs)
 *  - manager/admin/super_admin: global, dealer-wide, ALL salespeople's leads
 *    (GET /api/admin/autopilot/jobs)
 *
 * This mirrors the same scoping pattern already used for Hot Leads
 * (my-hot vs hot-available) — salespeople never see other salespeople's
 * lead activity; managers get the full picture.
 */

function formatDateTime(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('en-AU');
  } catch {
    return String(value);
  }
}

function statusColor(status) {
  const colors = {
    pending: '#64748b',
    claimed: 'var(--brand-primary, #3b82f6)',
    sent: '#22c55e',
    failed: '#ef4444',
    cancelled: '#94a3b8',
  };
  return colors[status] || '#64748b';
}

function AutopilotView({ isGlobal }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const data = isGlobal
        ? await api.getGlobalAutopilotJobs(status)
        : await api.getMyAutopilotJobs(status);
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load autopilot jobs:', e);
      setError(e?.message || 'Failed to load autopilot jobs');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, isGlobal]);

  const sortedJobs = useMemo(() => {
    return [...jobs].sort((a, b) => {
      const ad = new Date(a.scheduled_for || 0).getTime();
      const bd = new Date(b.scheduled_for || 0).getTime();
      return bd - ad;
    });
  }, [jobs]);

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'sent', label: 'Sent' },
    { id: 'failed', label: 'Failed' },
  ];

  if (loading) {
    return <div style={styles.loading}>Loading autopilot activity…</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Autopilot</h1>
          <p style={styles.subtitle}>
            {isGlobal
              ? `Global messaging across all leads · ${sortedJobs.length} jobs`
              : `Your leads' autopilot activity · ${sortedJobs.length} jobs`}
          </p>
        </div>
        <button onClick={load} style={styles.refreshBtn}>
          Refresh
        </button>
      </div>

      <div style={styles.filters}>
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            style={{
              ...styles.filterButton,
              ...(statusFilter === f.id ? styles.filterButtonActive : {}),
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={styles.errorBanner}>
          {error}
          <button onClick={() => setError(null)} style={styles.dismissBtn}>
            ×
          </button>
        </div>
      )}

      {sortedJobs.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <div style={styles.emptyText}>No autopilot activity{statusFilter !== 'all' ? ` (${statusFilter})` : ''}</div>
          <div style={styles.emptySubtext}>
            {isGlobal
              ? 'Scheduled and sent messages across all leads will show here.'
              : 'Scheduled and sent messages for your claimed leads will show here.'}
          </div>
        </div>
      ) : (
        <div style={styles.list}>
          {sortedJobs.map((job) => (
            <div key={job.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={styles.nameBlock}>
                  <div style={styles.name}>{job.contact_name || 'Unknown contact'}</div>
                  <div style={styles.channel}>{job.channel === 'sms' ? '📱 SMS' : '✉️ Email'}</div>
                </div>
                <span
                  style={{
                    ...styles.statusBadge,
                    backgroundColor: statusColor(job.status),
                  }}
                >
                  {job.status}
                </span>
              </div>

              {job.subject && <div style={styles.subject}>{job.subject}</div>}
              <div style={styles.body}>{job.body}</div>

              <div style={styles.metaRow}>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Scheduled</span>
                  <span style={styles.metaValue}>{formatDateTime(job.scheduled_for) || '—'}</span>
                </div>
                {job.sent_at && (
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Sent</span>
                    <span style={styles.metaValue}>{formatDateTime(job.sent_at)}</span>
                  </div>
                )}
                {job.status === 'failed' && job.last_error && (
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Error</span>
                    <span style={{ ...styles.metaValue, color: '#fca5a5' }}>{job.last_error}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '900px',
    padding: '20px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '8px',
  },
  title: {
    color: '#f1f5f9',
    margin: '0 0 6px 0',
    fontSize: '24px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#94a3b8',
    margin: 0,
    fontSize: '14px',
  },
  refreshBtn: {
    padding: '8px 16px',
    fontSize: '13px',
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    minHeight: '44px',
  },
  filters: {
    marginTop: '12px',
    marginBottom: '18px',
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  filterButton: {
    padding: '8px 16px',
    fontSize: '14px',
    background: '#1e293b',
    color: 'var(--brand-secondary, #94a3b8)',
    border: '1px solid var(--brand-secondary, #334155)',
    borderRadius: '20px',
    cursor: 'pointer',
  },
  filterButtonActive: {
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    borderColor: 'var(--brand-primary, #3b82f6)',
  },
  loading: {
    color: '#94a3b8',
    fontSize: '14px',
    padding: '20px',
  },
  errorBanner: {
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#fca5a5',
    padding: '12px 16px',
    borderRadius: '8px',
    margin: '16px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#fca5a5',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 32px',
    background: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
    marginTop: '18px',
  },
  emptyIcon: {
    fontSize: '56px',
    marginBottom: '12px',
  },
  emptyText: {
    color: '#94a3b8',
    fontSize: '16px',
    marginBottom: '8px',
  },
  emptySubtext: {
    color: '#64748b',
    fontSize: '13px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  card: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #334155',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  nameBlock: {
    minWidth: 0,
    flex: 1,
  },
  name: {
    color: '#f1f5f9',
    fontSize: '16px',
    fontWeight: '600',
  },
  channel: {
    color: '#64748b',
    fontSize: '12px',
    marginTop: '2px',
  },
  statusBadge: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#fff',
    padding: '4px 10px',
    borderRadius: '999px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  subject: {
    color: '#e2e8f0',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '6px',
  },
  body: {
    color: '#cbd5e1',
    fontSize: '13px',
    lineHeight: 1.4,
    background: 'rgba(15, 23, 42, 0.35)',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    padding: '10px 12px',
    borderRadius: '10px',
    marginBottom: '10px',
    whiteSpace: 'pre-wrap',
  },
  metaRow: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    paddingTop: '10px',
    borderTop: '1px solid #334155',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  metaLabel: {
    color: '#64748b',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  metaValue: {
    color: '#94a3b8',
    fontSize: '12px',
  },
};

export default AutopilotView;
